/**
 * Seenly Jewelry — Wishlist System
 * Stores customer saved items in localStorage and manages Wishlist Drawer & Header Count
 */
class SeenlyWishlistManager {
  constructor() {
    this.storageKey = 'seenly_wishlist_v1';
    this.init();
  }

  init() {
    this.updateBadges();
    this.bindEvents();
    // Render drawer when opened
    document.addEventListener('DOMContentLoaded', () => {
      this.updateBadges();
      this.renderDrawer();
    });
    // Listen to custom drawer show event
    const drawer = document.getElementById('wishlist-drawer');
    if (drawer) {
      drawer.addEventListener('toggle', () => {
        if (drawer.open) {
          this.renderDrawer();
        }
      });
      // also mutation observer for attribute open
      const observer = new MutationObserver(() => {
        if (drawer.hasAttribute('open')) {
          this.renderDrawer();
        }
      });
      observer.observe(drawer, { attributes: true, attributeFilter: ['open'] });
    }
  }

  getItems() {
    try {
      const data = localStorage.getItem(this.storageKey);
      return data ? JSON.parse(data) : [];
    } catch (e) {
      console.warn('[Wishlist] Storage read error:', e);
      return [];
    }
  }

  saveItems(items) {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(items));
      this.updateBadges();
      this.renderDrawer();
      window.dispatchEvent(new CustomEvent('seenly:wishlist:updated', { detail: { items } }));
    } catch (e) {
      console.warn('[Wishlist] Storage save error:', e);
    }
  }

  has(id) {
    const items = this.getItems();
    return items.some(item => String(item.id) === String(id));
  }

  add(item) {
    if (!item || !item.id) return;
    const items = this.getItems();
    if (!this.has(item.id)) {
      items.unshift({
        id: String(item.id),
        title: item.title || 'Artisan Pearl Jewelry',
        price: item.price || '',
        price_formatted: item.price_formatted || item.price || '',
        image: item.image || '',
        url: item.url || '/collections/all',
        variant_id: item.variant_id || item.id,
        created_at: Date.now()
      });
      this.saveItems(items);
    }
  }

  remove(id) {
    const items = this.getItems().filter(item => String(item.id) !== String(id));
    this.saveItems(items);
  }

  toggle(item) {
    if (this.has(item.id)) {
      this.remove(item.id);
      return false;
    } else {
      this.add(item);
      return true;
    }
  }

  updateBadges() {
    const count = this.getItems().length;
    document.querySelectorAll('[data-wishlist-header-count]').forEach(el => {
      el.textContent = count;
      if (count > 0) {
        el.classList.remove('hidden');
      } else {
        el.classList.add('hidden');
      }
    });

    document.querySelectorAll('[data-wishlist-drawer-count]').forEach(el => {
      el.textContent = count === 1 ? '1 item' : `${count} items`;
    });

    // Update any active heart toggles on page
    document.querySelectorAll('[data-wishlist-toggle]').forEach(btn => {
      const id = btn.getAttribute('data-product-id');
      if (id && this.has(id)) {
        btn.classList.add('active', 'text-accent');
        btn.setAttribute('aria-pressed', 'true');
      } else {
        btn.classList.remove('active', 'text-accent');
        btn.setAttribute('aria-pressed', 'false');
      }
    });
  }

  renderDrawer() {
    const container = document.getElementById('wishlist-drawer-items');
    if (!container) return;

    const items = this.getItems();
    if (items.length === 0) {
      container.innerHTML = `
        <div class="flex flex-col items-center justify-center py-16 px-6 text-center">
          <div class="size-16 rounded-full bg-surface-secondary flex items-center justify-center text-on-surface-secondary mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-8">
              <path stroke-linecap="round" stroke-linejoin="round" d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12Z" />
            </svg>
          </div>
          <h3 class="font-heading font-medium text-lg text-on-surface mb-2">Your Wishlist is Empty</h3>
          <p class="text-xs text-on-surface-secondary max-w-xs mb-6 leading-relaxed">
            Save your favorite handcrafted freshwater pearls and bespoke keepsakes to review and order anytime.
          </p>
          <a href="/collections/all" class="button-primary px-6 py-2.5 rounded-button text-xs font-semibold tracking-wider uppercase">
            Explore All Jewelry
          </a>
        </div>
      `;
      return;
    }

    let html = '<div class="divide-y divide-border -mt-2">';
    items.forEach(item => {
      html += `
        <div class="py-4 flex gap-4 items-center group/wish-item" data-wishlist-row="${item.id}">
          <a href="${item.url}" class="shrink-0 size-20 rounded-lg overflow-hidden border border-border/80 bg-surface-secondary block">
            ${item.image ? `<img src="${item.image}" alt="${item.title}" class="size-full object-cover group-hover/wish-item:scale-105 transition-transform" />` : '<div class="size-full flex items-center justify-center text-xs text-on-surface-secondary">Jewelry</div>'}
          </a>
          <div class="flex-1 min-w-0 flex flex-col justify-between">
            <div>
              <a href="${item.url}" class="text-xs sm:text-sm font-semibold text-on-surface hover:text-accent transition-colors line-clamp-1 block">
                ${item.title}
              </a>
              ${item.price_formatted ? `<p class="text-xs text-on-surface font-medium mt-0.5">${item.price_formatted}</p>` : ''}
            </div>
            <div class="flex items-center gap-2 mt-2">
              <button type="button" class="px-3 py-1.5 rounded-md bg-accent text-white hover:bg-accent/90 text-xs font-semibold uppercase tracking-wider transition-all" data-wishlist-add-to-cart="${item.variant_id || item.id}">
                Add to Cart
              </button>
              <button type="button" class="p-1.5 text-on-surface-secondary hover:text-critical transition-colors text-xs" data-wishlist-remove="${item.id}" aria-label="Remove item">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-4">
                  <path stroke-linecap="round" stroke-linejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      `;
    });
    html += '</div>';
    container.innerHTML = html;
  }

  bindEvents() {
    // Delegate click inside wishlist drawer
    document.addEventListener('click', async (e) => {
      // Remove button
      const removeBtn = e.target.closest('[data-wishlist-remove]');
      if (removeBtn) {
        const id = removeBtn.getAttribute('data-wishlist-remove');
        if (id) {
          this.remove(id);
        }
        return;
      }

      // Add to cart from wishlist
      const addCartBtn = e.target.closest('[data-wishlist-add-to-cart]');
      if (addCartBtn) {
        const variantId = addCartBtn.getAttribute('data-wishlist-add-to-cart');
        if (variantId) {
          const originalText = addCartBtn.textContent;
          addCartBtn.disabled = true;
          addCartBtn.textContent = 'Adding...';
          try {
            const res = await fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ id: Number(variantId), quantity: 1 })
            });
            if (res.ok) {
              addCartBtn.textContent = 'Added ✓';
              setTimeout(() => {
                addCartBtn.textContent = originalText;
                addCartBtn.disabled = false;
              }, 1500);
              // Trigger cart update if s-cart exists
              window.dispatchEvent(new CustomEvent('cart:updated'));
              // Open cart drawer
              const cartTrigger = document.getElementById('header-cart-trigger');
              if (cartTrigger) {
                cartTrigger.click();
              }
            } else {
              addCartBtn.textContent = 'Unavailable';
              setTimeout(() => {
                addCartBtn.textContent = originalText;
                addCartBtn.disabled = false;
              }, 2000);
            }
          } catch (err) {
            console.error('[Wishlist] Cart add error:', err);
            addCartBtn.textContent = originalText;
            addCartBtn.disabled = false;
          }
        }
        return;
      }

      // Toggle wishlist item from product card or detail page
      const toggleBtn = e.target.closest('[data-wishlist-toggle]');
      if (toggleBtn) {
        e.preventDefault();
        e.stopPropagation();
        const id = toggleBtn.getAttribute('data-product-id');
        const title = toggleBtn.getAttribute('data-product-title');
        const price = toggleBtn.getAttribute('data-product-price');
        const image = toggleBtn.getAttribute('data-product-image');
        const url = toggleBtn.getAttribute('data-product-url');
        const variantId = toggleBtn.getAttribute('data-variant-id') || id;

        if (id) {
          this.toggle({ id, title, price_formatted: price, image, url, variant_id: variantId });
        }
      }
    });
  }
}

// Global instance
window.SeenlyWishlist = new SeenlyWishlistManager();
export default window.SeenlyWishlist;
